import { renderToString } from 'react-dom/server';

import { LEGEND_CONTENT, CONCENTRATION_LEVELS } from './Config';

export const isDevMode = () => {
	return (process.env.NODE_ENV === 'development');
}

export const isIframe = () => {
	return typeof window !== 'undefined' && window.self !== window.top;
}

export const csvToJSON = (json, sheetId) => {
	if (!json) return [];

	let arrayOfObjects = [];
	let rows = json?.values || [];
	let header = rows.shift();
	for (const row of rows) {
		const obj = {}
		for (const [index, cell] of row.entries()) {
			obj[header[index]] = cell;
		}
		if (sheetId) obj.sheetId = sheetId;
		arrayOfObjects.push(obj)
	}
	
	return arrayOfObjects;
}

export const isMobile = () => {
	let check = false;
	// eslint-disable-next-line no-useless-escape
	(function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||window.opera);
	if (window.innerWidth <= 600) { check = true; };
	return check;
};

export const getTileLayerId = (props) => {
	const { pattern, year, type } = props;
	let layerName = pattern.replace('{YEAR}', year).replace('{TYPE}', type);
	return layerName;
}

export const generateGradient = () => {
	let gradientData = ['90deg'];
	const colors = LEGEND_CONTENT.map(layer => layer.color);
	const step = Math.ceil(100/colors.length);
	colors.forEach((color, index) => { gradientData.push(`${color} ${index*step}%`); });
	gradientData.push(`${colors[colors.length-1]} 100%`);
	const gradient = `linear-gradient(${gradientData.join(', ')})`;
	return gradient;
}

export const getTextContrast = (color) => {
	if (color) {
		const red = parseInt(color.substring(1,3),16);
		const green = parseInt(color.substring(3,5),16);
		const blue = parseInt(color.substring(5,7),16);
		const result = ((red*299)+(green*587)+(blue*114))/1000;
		return (result >= 128) ? 'black' : 'white';
	} else {
		return 'black';
	}
}

export const getConcentrationLevel = (value) => {
	let foundIndex = 0;
	Object.entries(CONCENTRATION_LEVELS).every((level) => {
		let index = level[0];
		let startValue = level[1].startValue;
		let endValue = level[1].endValue;
		if (value >= startValue && value < endValue) {
			foundIndex = index || 0;
			return false;
		}
		return true;
	});
	// If our value is higher than values in the CONCENTRATION_LEVELS object
	if (foundIndex === 0 && value > CONCENTRATION_LEVELS[CONCENTRATION_LEVELS.length-1].startValue) foundIndex = CONCENTRATION_LEVELS.length-1;
	return CONCENTRATION_LEVELS[foundIndex] || null;
}

export const constructPopupContent = ({data, language, toString}) => {
	const props = data.properties || data.feature || null

	const concentrationValueRaw = parseFloat(props?.['Nitrat']) || 0;
	const concentrationValue = Math.floor(concentrationValueRaw*10)/10;
	const concentrationLevel = getConcentrationLevel(concentrationValue);

	const { description, healthSentence, healthIcons } = concentrationLevel;
	const levelId = concentrationLevel.id;
	const bgColor = concentrationLevel.color;

	const content = (
		<div className="nitratedata-content-wrapper">
			<div className='sidebar-header'>
				<div className='sidebar-title'>
					<div>
						{(language) ? language[`popup:header`] : 'Title'}
					</div>
				</div>
			</div>
			
			<div className="nitratedata-average-wrapper">
				<div className="title">
					<div>{language ? language[`popup:concentration_level:${levelId}:description`] : description}</div>

					{(healthIcons.length > 0) &&
						<div className="nitratedata-health-icons">
							{healthIcons.map((icon, index) => {
								return (
									<div
										className='nitrate-health-icon-wrapper'
										key={index}
										data-tippy-placement='bottom'
										data-tippy-content={(language) ? language[`tooltip:health:${icon}`] : ''}
									>
										<div className={`nitratedata-health-icon ${icon}`}></div>
										<div className='nitratedata-health-icon-text'>{(language) ? language[`hovertooltip:icons:${icon}`] : ''}</div>
									</div>
								);
							})}
						</div>
					}
				</div>
				<div className="value-wrapper">
					<div
						className="value"
						style={{
							color: getTextContrast(bgColor),
							backgroundColor: bgColor
						}}
					>
						{concentrationValue}
						<span
							className="units"
							dangerouslySetInnerHTML={{ __html: language?.['popup:units'] ?? 'units' }}
						/>
					</div>
				</div>
			</div>

			<div
				className="nitratedata-description"
				dangerouslySetInnerHTML={{
					__html: language ? language[`popup:concentration_level:${levelId}:health`] : healthSentence
				}}
			/>
		</div>
	);

	return toString ? renderToString(content) : content;
}
